/**
 * MobileBottomNav — Fixed bottom tab bar for mobile (visible < md only)
 *
 * Scroll morph: full bar → floating pill on scroll down, pill → bar on scroll up.
 * Performance: ref-based class toggle (no React re-renders during scroll).
 * CSS handles all transitions (see .btm-nav-* classes in tailwind.css).
 */

import { useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  House,
  ChatCircleDots,
  ShoppingBag,
  UserCircle,
  Lifebuoy,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { useSupportOnly } from "@/hooks/useSupportOnly";

interface Tab {
  label: string;
  href: string;
  icon?: typeof House;
  isStarburst?: boolean;
}

const TABS: Tab[] = [
  { label: "\u0e2b\u0e19\u0e49\u0e32\u0e2b\u0e25\u0e31\u0e01", href: "/", icon: House },
  { label: "\u0e41\u0e0a\u0e17", href: "/inbox", icon: ChatCircleDots },
  { label: "\u0e40\u0e21\u0e19\u0e39", href: "/menu", isStarburst: true },
  { label: "\u0e04\u0e33\u0e2a\u0e31\u0e48\u0e07\u0e0b\u0e37\u0e49\u0e2d", href: "/orders", icon: ShoppingBag },
  { label: "\u0e1a\u0e31\u0e0d\u0e0a\u0e35", href: "/settings", icon: UserCircle },
];

// Support-only tenants (e.g. heybay): only the Help Center + account.
const SUPPORT_ONLY_TABS: Tab[] = [
  { label: "\u0e28\u0e39\u0e19\u0e22\u0e4c\u0e0a\u0e48\u0e27\u0e22\u0e40\u0e2b\u0e25\u0e37\u0e2d", href: "/support", icon: Lifebuoy },
  { label: "\u0e1a\u0e31\u0e0d\u0e0a\u0e35", href: "/settings", icon: UserCircle },
];

export function MobileBottomNav() {
  const { pathname } = useLocation();
  const prevPathRef = useRef(pathname);
  const replayRef = useRef(0);
  const { view } = useOnboardingState();
  const supportOnly = useSupportOnly();
  const tabs = supportOnly ? SUPPORT_ONLY_TABS : TABS;

  // DOM refs for direct class toggle (no re-renders)
  const navRef = useRef<HTMLElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const isPill = useRef(false);
  const rafRef = useRef(0);

  // Trigger starburst replay when navigating to or from /menu
  useEffect(() => {
    const wasMenu = prevPathRef.current.startsWith("/menu");
    const isMenu = pathname.startsWith("/menu");
    if (wasMenu !== isMenu) {
      replayRef.current += 1;
    }
    prevPathRef.current = pathname;
  }, [pathname]);

  // Toggle bar ↔ pill via class (no React state, no re-render)
  const applyState = useCallback((pill: boolean) => {
    if (isPill.current === pill) return;
    isPill.current = pill;
    const nav = navRef.current;
    const inner = innerRef.current;
    if (!nav || !inner) return;

    if (pill) {
      nav.classList.add("btm-pill");
      // Pill: floating centered capsule
      nav.style.background = "transparent";
      nav.style.borderColor = "transparent";
      nav.style.padding = "0 24px 8px";
      inner.style.maxWidth = "280px";
      inner.style.height = "48px";
      inner.style.background = "hsl(var(--background))";
      inner.style.borderRadius = "9999px";
      inner.style.boxShadow = "0 4px 24px rgba(0,0,0,0.12)";
      inner.style.padding = "0 8px";
    } else {
      nav.classList.remove("btm-pill");
      // Bar: full-width tab bar
      nav.style.background = "";
      nav.style.borderColor = "";
      nav.style.padding = "";
      inner.style.maxWidth = "";
      inner.style.height = "";
      inner.style.background = "";
      inner.style.borderRadius = "";
      inner.style.boxShadow = "";
      inner.style.padding = "";
    }
  }, []);

  // Hide on scroll down (→ pill), show on scroll up (→ bar)
  useEffect(() => {
    const update = (y: number) => {
      const shouldPill = y > lastScrollY.current && y > 56;
      lastScrollY.current = y;
      // Batch with rAF — one DOM write per frame max
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        applyState(shouldPill);
      });
    };
    const onWindowScroll = () => update(window.scrollY);
    const onPanelScroll = (e: Event) => {
      const scrollTop = (e as CustomEvent).detail?.scrollTop ?? 0;
      update(scrollTop);
    };
    window.addEventListener("scroll", onWindowScroll, { passive: true });
    window.addEventListener("panel-scroll", onPanelScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onWindowScroll);
      window.removeEventListener("panel-scroll", onPanelScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [applyState]);

  // Hide bottom nav during intent onboarding — full-screen focus
  if (view === "intent-onboarding") return null;

  return (
    <nav
      ref={navRef}
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 pb-[env(safe-area-inset-bottom)] bg-background border-t border-gray-200/60 dark:border-gray-800/60 btm-nav-outer"
    >
      <div
        ref={innerRef}
        className="flex items-center justify-around h-14 max-w-[600px] mx-auto btm-nav-inner"
      >
        {tabs.map(({ label, href, icon: Icon, isStarburst }) => {
          const isActive =
            href === "/" ? pathname === "/" : pathname.startsWith(href);

          return (
            <Link
              key={href}
              to={href}
              className={cn(
                "flex flex-col items-center justify-center flex-1 gap-0.5 h-full",
                "transition-colors duration-150",
                isActive
                  ? "text-primary"
                  : "text-gray-400 dark:text-gray-500 active:text-gray-600"
              )}
            >
              {isStarburst ? (
                <AnimatedStarburst size={24} replayTrigger={replayRef.current} />
              ) : Icon ? (
                <Icon size={24} weight={isActive ? "fill" : "regular"} />
              ) : null}
              <span className="text-[10px] font-medium leading-none btm-nav-label">
                {label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
